import { restoreDialogFocus } from './focus';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ConfirmContext, type Confirmation } from './confirm-context';
import { ResponsiveDialog } from './dialog';
import { Button } from './button';

export function ConfirmProvider({
  children,
  resetKey,
}: {
  children: ReactNode;
  resetKey?: string;
}): JSX.Element {
  const [dialog, setDialog] = useState<Confirmation | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const resolver = useRef<((confirmed: boolean) => void) | null>(null);
  const settle = useCallback((value: boolean): void => {
    const returnFocus = opener.current;
    opener.current = null;
    resolver.current?.(value);
    resolver.current = null;
    setDialog(null);
    // Awaiting controls become enabled again in the promise microtask, before this frame.
    restoreDialogFocus(returnFocus);
  }, []);
  const confirm = useCallback((options: Confirmation): Promise<boolean> => {
    if (resolver.current) return Promise.resolve(false);
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return new Promise((resolve) => {
      resolver.current = resolve;
      setDialog(options);
    });
  }, []);
  useEffect(() => {
    if (resolver.current) settle(false);
  }, [resetKey, settle]);
  useEffect(
    () => () => {
      resolver.current?.(false);
    },
    [],
  );
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <ResponsiveDialog
          title={dialog.title}
          closeLabel={dialog.cancelLabel}
          onClose={() => settle(false)}
          footer={
            <div className="giving-actions">
              <Button variant="secondary" onClick={() => settle(false)}>
                {dialog.cancelLabel}
              </Button>
              <Button onClick={() => settle(true)}>{dialog.confirmLabel}</Button>
            </div>
          }
        >
          <p>{dialog.message}</p>
        </ResponsiveDialog>
      )}
    </ConfirmContext.Provider>
  );
}
